<template>
  <v-content>
    <v-container>
      <v-card>
        <v-card-title>
          <v-icon>fa-building</v-icon> Puestos de Control:
          <v-spacer/>
          <v-text-field
            v-model="search"
            append-icon="search"
            label="Buscar"
            single-line
            hide-details
          />
          <v-btn @click="openModal()" class="mx-2 py-0" color="primary">
            <v-icon>add_circle</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-data-table
          :headers="headers"
          :items="puestos"
          :items-per-page="10"
          :search="search"
          class="elevation-1"
        >
          <template v-slot:item.active="{ item }">
            <v-chip :color="item.active ? 'success' : 'error'" dark>
              {{ item.active ? 'Activo' : 'Inactivo' }}
            </v-chip>
          </template>
          
          <!-- NUEVA COLUMNA: Mostrar emails -->
          <template v-slot:item.emails="{ item }">
            <div class="emails-cell">
              <v-chip
                v-for="(email, index) in getEmails(item)"
                :key="index"
                x-small
                class="ma-1"
                color="info"
              >
                {{ email }}
              </v-chip>
              <span v-if="getEmails(item).length === 0" class="grey--text text--lighten-1">
                Sin emails
              </span>
            </div>
          </template>
          
          <template v-slot:item.zonesCount="{ item }">
            <v-chip color="info" dark>
              {{ getZonesCount(item.id) }} zonas
            </v-chip>
          </template>
          
          <template v-slot:item.actions="{ item }">
            <v-icon
              small
              class="mr-2"
              @click="editItem(item)"
            >
              edit
            </v-icon>
            <v-icon
              small
              @click="deleteItem(item)"
            >
              delete
            </v-icon>
          </template>
        </v-data-table>
      </v-card>

      <!-- Modal Create/Edit -->
      <v-dialog v-model="dialog" width="600">
        <ValidationObserver ref="obs" v-slot="{ handleSubmit }">
          <v-card>
            <v-card-title class="headline text-center">
              {{ edit ? 'Editar' : 'Crear' }} Puesto
            </v-card-title>
            
            <v-form
              @submit.prevent="handleSubmit(formSubmit)"
              ref="form"
              lazy-validation
              class="container"
            >
              <v-row no-gutters>
                <v-col cols="12">
                  <ValidationProvider rules="required" name="name" v-slot="{ errors, valid }">
                    <v-text-field
                      v-model="puesto.name"
                      name="name"
                      label="Nombre del Puesto:"
                      placeholder="Ej: Puesto 1"
                      :error-messages="errors"
                      :success="valid"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>
              
              <!-- NUEVO: Campo para agregar múltiples emails -->
              <v-row no-gutters>
                <v-col cols="12">
                  <v-text-field
                    v-model="newEmail"
                    name="email"
                    label="Email para Reportes:"
                    placeholder="supervisor@ejemplo.com"
                    type="email"
                    @keyup.enter="addEmail"
                    append-icon="add"
                    @click:append="addEmail"
                    hint="Presiona Enter o clic en + para agregar múltiples emails"
                    persistent-hint
                  />
                </v-col>
              </v-row>
              
              <!-- NUEVO: Mostrar emails agregados como chips -->
              <v-row no-gutters v-if="puesto.emails && puesto.emails.length > 0">
                <v-col cols="12">
                  <div class="mt-3 mb-2">
                    <small class="grey--text">Emails configurados:</small>
                    <div class="mt-2">
                      <v-chip
                        v-for="(email, index) in puesto.emails"
                        :key="index"
                        class="ma-1"
                        close
                        color="primary"
                        @click:close="removeEmail(index)"
                      >
                        <v-icon left small>email</v-icon>
                        {{ email }}
                      </v-chip>
                    </div>
                  </div>
                </v-col>
              </v-row>
              
              <!-- ADVERTENCIA: Si no hay emails -->
              <v-row no-gutters v-if="!puesto.emails || puesto.emails.length === 0">
                <v-col cols="12">
                  <v-alert type="warning" dense text>
                    Debes agregar al menos un email para recibir reportes
                  </v-alert>
                </v-col>
              </v-row>
              
              <v-row no-gutters v-if="edit">
                <v-col cols="12">
                  <v-switch
                    v-model="puesto.active"
                    label="Puesto Activo"
                    color="success"
                  />
                </v-col>
              </v-row>
              
              <v-card-actions>
                <v-spacer/>
                <v-btn @click="dialog = false">Cancelar</v-btn>
                <v-btn 
                  type="submit" 
                  color="primary"
                  :disabled="!puesto.emails || puesto.emails.length === 0"
                >
                  {{ edit ? 'Actualizar' : 'Guardar' }}
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </ValidationObserver>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar color="success" v-model="snackbar">
        {{ text }}
        <v-btn color="white" text @click="snackbar = false">
          Cerrar
        </v-btn>
      </v-snackbar>
    </v-container>
  </v-content>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { ValidationObserver } from "vee-validate"

export default {
  name: "Puestos",
  data() {
    return {
      search: '',
      dialog: false,
      edit: false,
      snackbar: false,
      text: '',
      idPuesto: null,
      newEmail: '', // NUEVO: Para el input temporal de email
      puesto: {
        name: '',
        emails: [], // CAMBIADO: de email a emails (array)
        active: true
      },
      headers: [
        { text: 'Nombre', align: 'left', value: 'name' },
        { text: 'Emails', value: 'emails', sortable: false }, // CAMBIADO
        { text: 'Zonas Asignadas', value: 'zonesCount', sortable: false },
        { text: 'Estado', value: 'active' },
        { text: 'Opciones', value: 'actions', sortable: false }
      ]
    }
  },
  
  components: {
    ValidationObserver
  },
  
  mounted() {
    this.bindPuestos()
    this.bindZones()
  },
  
  methods: {
    ...mapActions({
      bindPuestos: 'puesto/bindPuestos',
      createPuesto: 'puesto/createPuesto',
      updatePuesto: 'puesto/updatePuesto',
      deletePuesto: 'puesto/deletePuesto',
      bindZones: 'zone/bindZones'
    }),
    
    // NUEVO: Obtener emails (retrocompatible con campo antiguo 'email')
    getEmails(item) {
      // Si tiene el nuevo campo 'emails' (array)
      if (item.emails && Array.isArray(item.emails)) {
        return item.emails;
      }
      // Si tiene el campo antiguo 'email' (string)
      if (item.email && typeof item.email === 'string') {
        return [item.email];
      }
      return [];
    },
    
    // NUEVO: Método para agregar email
    addEmail() {
      const email = this.newEmail.trim();
      
      // Validar que el email no esté vacío
      if (!email) {
        return;
      }
      
      // Validar formato de email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        this.text = 'Por favor ingresa un email válido';
        this.snackbar = true;
        return;
      }
      
      // Validar que no esté duplicado
      if (this.puesto.emails.includes(email)) {
        this.text = 'Este email ya está agregado';
        this.snackbar = true;
        return;
      }
      
      // Agregar email al array
      this.puesto.emails.push(email);
      this.newEmail = ''; // Limpiar el input
    },
    
    // NUEVO: Método para remover email
    removeEmail(index) {
      this.puesto.emails.splice(index, 1);
    },
    
    getZonesCount(puestoId) {
      return this.zones.filter(z => z.puestoId === puestoId).length
    },
    
    openModal() {
      this.dialog = true
      this.edit = false
      this.puesto = {
        name: '',
        emails: [], // CAMBIADO
        active: true
      }
      this.newEmail = '' // NUEVO: Limpiar el input temporal
      this.idPuesto = null
    },
    
    editItem(item) {
      this.edit = true
      this.puesto = { 
        ...item,
        // IMPORTANTE: Usar getEmails para manejar retrocompatibilidad
        emails: this.getEmails(item)
      }
      this.newEmail = '' // NUEVO
      this.idPuesto = item.id
      this.dialog = true
    },
    
    async deleteItem(item) {
      if (confirm(`¿Está seguro de eliminar el puesto ${item.name}?`)) {
        try {
          await this.deletePuesto(item.id)
          this.text = 'Puesto eliminado exitosamente'
          this.snackbar = true
        } catch (error) {
          this.text = 'Error al eliminar el puesto'
          this.snackbar = true
        }
      }
    },
    
    async formSubmit() {
      // Validar que haya al menos un email
      if (!this.puesto.emails || this.puesto.emails.length === 0) {
        this.text = 'Debes agregar al menos un email'
        this.snackbar = true
        return
      }
      
      try {
        if (this.edit) {
          await this.updatePuesto({ 
            id: this.idPuesto, 
            data: this.puesto 
          })
          this.text = 'Puesto actualizado exitosamente'
        } else {
          await this.createPuesto(this.puesto)
          this.text = 'Puesto creado exitosamente'
        }
        this.dialog = false
        this.snackbar = true
      } catch (error) {
        this.text = 'Error al guardar el puesto'
        this.snackbar = true
      }
    }
  },
  
  computed: {
    ...mapState({
      puestos: state => state.puesto.puestos,
      zones: state => state.zone.zones || []
    })
  }
}
</script>

<style scoped>
.emails-cell {
  max-width: 300px;
}
</style>